import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Hash } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { PostalArea } from '@/hooks/usePostalAreas';

type PostalAreaLike = Pick<PostalArea, 'id' | 'name' | 'slug' | 'code' | 'population_total' | 'postcode_count'> & {
  area: number | null;
};

interface PostalAreaGridProps {
  areas: PostalAreaLike[];
  buildHref: (area: PostalAreaLike) => string;
  childLabel: string;
}

export function PostalAreaGrid({ areas, buildHref, childLabel }: PostalAreaGridProps) {
  const { t } = useTranslation();

  return (
    <div>
      <h2 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">{childLabel} ({areas.length})</h2>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs md:text-sm px-2 md:px-4">{t('postal.table.name')}</TableHead>
                <TableHead className="text-right text-xs md:text-sm px-2 md:px-4 whitespace-nowrap">{t('postal.stats.population')}</TableHead>
                <TableHead className="text-right text-xs md:text-sm px-2 md:px-4 whitespace-nowrap"><span className="sm:hidden">Postnr</span><span className="hidden sm:inline">{t('postal.stats.postalCodes')}</span></TableHead>
                <TableHead className="text-right text-xs md:text-sm px-2 md:px-4 hidden sm:table-cell">{t('postal.stats.area')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {areas.map((area) => (
                <TableRow key={area.id} className="cursor-pointer">
                  <TableCell className="px-2 md:px-4 py-2 md:py-4 text-xs md:text-sm">
                    <Link to={buildHref(area)} className="text-primary hover:underline font-medium">
                      {area.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-right px-2 md:px-4 py-2 md:py-4 text-xs md:text-sm whitespace-nowrap">{area.population_total.toLocaleString('sv-SE')}</TableCell>
                  <TableCell className="text-right px-2 md:px-4 py-2 md:py-4 text-xs md:text-sm whitespace-nowrap">{area.postcode_count}</TableCell>
                  <TableCell className="text-right px-2 md:px-4 py-2 md:py-4 text-xs md:text-sm hidden sm:table-cell whitespace-nowrap">{area.area && area.area > 0 ? `${area.area.toLocaleString('sv-SE')} km²` : '–'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
